import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { LeadersFilters, User, VoterGroup } from '../../../types';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';

import Papa from 'papaparse';
import { db } from '../../../firebase';
import useAuth from '../../../hooks/useAuth';

interface LeadersPrecinctReportProps {
  filters: LeadersFilters
}

const LeadersPrecinctReport: React.FC<LeadersPrecinctReportProps> = ({ filters }) => {
  const { user } = useAuth();
  
  const [voterGroups, setVoterGroups] = useState<VoterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVotersAndPolls = async (user: User) => {
      try {
        setLoading(true);

        const voterGroupsCollection = collection(db, 'voterGroups');
        let voterGroupsQuery = query(voterGroupsCollection, orderBy('name'));
        voterGroupsQuery = query(voterGroupsQuery, where('city_municipality', '==', filters.municipality));
        voterGroupsQuery = query(voterGroupsQuery, where('barangay', '==', filters.barangay));
        const vgQuerySnapshot = await getDocs(voterGroupsQuery);
        const _voterGroups = vgQuerySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as VoterGroup);
        setVoterGroups(_voterGroups);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching voters and polls:', err);
        setError('Failed to fetch voters and polls');
        setLoading(false);
      }
    };

    if (user && filters.region && filters.province &&
      filters.district && filters.municipality && filters.barangay
    ) {
      fetchVotersAndPolls(user);
    }
  }, [user, filters]);

  // Updated export to CSV function to include each member in a separate row
  const exportToCSV = () => {
    // Prepare CSV data with each member in a separate row
    const csvData = voterGroups.flatMap((group, groupIndex) => 
      group.members.map((member, memberIndex) => ({
        '#': groupIndex + 1,
        'Leader': group.leader.name,
        'Member': member.name,
        'Municipality': group.city_municipality,
        'Barangay': group.barangay,
      }))
    );

    // Generate CSV file
    const csv = Papa.unparse(csvData);
    
    // Create and download CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leaders_${filters.barangay}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              Leaders in {filters.barangay}
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={exportToCSV}
              disabled={voterGroups.length === 0}
            >
              Export to CSV
            </Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Leader</TableCell>
                <TableCell>Members</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voterGroups.map((group, index) => (
                <TableRow key={group.id}>
                  <TableCell>{++index}</TableCell>
                  <TableCell>{group.leader.name}</TableCell>
                  <TableCell>
                    {group.members.map((m, memberIndex) => (
                      <div key={memberIndex}>{memberIndex + 1}. {m.name}</div>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default LeadersPrecinctReport;
